import React, { createContext, useState, useEffect, useContext } from 'react';

// Create the Theme Context
const ThemeContext = createContext({
  isDarkMode: false,
  toggleTheme: () => {},
  isSidebarExpanded: true,
  toggleSidebar: () => {}
});

// Theme Provider component
export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or system preference
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark' ||
      (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  // Initialize sidebar state from localStorage (expanded by default)
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(() => {
    const saved = localStorage.getItem('sidebarExpanded');
    return saved === null ? true : saved === 'true';
  });

  // Apply theme attributes to document when theme changes
  useEffect(() => {
    const theme = isDarkMode ? 'dark' : 'light';
    // data-theme drives our custom CSS variables
    document.documentElement.setAttribute('data-theme', theme);
    // data-bs-theme keeps Bootstrap's dark/light utilities working
    document.documentElement.setAttribute('data-bs-theme', theme);
    localStorage.setItem('theme', theme);
  }, [isDarkMode]);

  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem('sidebarExpanded', String(isSidebarExpanded));
  }, [isSidebarExpanded]);

  const toggleTheme = () => setIsDarkMode(prev => !prev);
  const toggleSidebar = () => setIsSidebarExpanded(prev => !prev);

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme, isSidebarExpanded, toggleSidebar }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook for using the theme context
export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
